import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { getAssistantSuggestions, askAssistant } from "@/api";

const SID_KEY = "ruach_assist_sid";
const ONBOARDED_KEY = "ruach_onboarded";

export const AssistantChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, text}
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SID_KEY) || "");
  const loadedRef = useRef(false);
  const scrollRef = useRef(null);

  // First-visit onboarding: auto-open once.
  useEffect(() => {
    if (!localStorage.getItem(ONBOARDED_KEY)) {
      const t = setTimeout(() => setOpen(true), 900);
      localStorage.setItem(ONBOARDED_KEY, "1");
      return () => clearTimeout(t);
    }
  }, []);

  // Load welcome + suggestions the first time the panel opens.
  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    getAssistantSuggestions()
      .then((d) => {
        setSuggestions(d.suggestions || []);
        setMessages((m) => (m.length ? m : [{ role: "assistant", text: d.welcome }]));
      })
      .catch(() => {
        setMessages((m) => (m.length ? m : [{ role: "assistant", text: "Hi! Ask me anything about getting started with R.U.A.C.H." }]));
      });
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setSending(true);
    try {
      const res = await askAssistant({ session_id: sessionId || null, message: q });
      if (res.session_id && res.session_id !== sessionId) {
        setSessionId(res.session_id);
        localStorage.setItem(SID_KEY, res.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", text: res.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry — I'm having trouble right now. Please try again in a moment." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          data-testid="assistant-fab"
          onClick={() => setOpen(true)}
          className="group fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-3 text-sm font-semibold text-white shadow-xl shadow-blue-900/40 transition-transform hover:scale-105"
        >
          <MessageCircle className="h-5 w-5" />
          <span className="hidden sm:inline">Need help?</span>
        </button>
      )}

      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-5 right-5 z-50 flex h-[560px] max-h-[85vh] w-[94vw] max-w-sm flex-col overflow-hidden rounded-2xl border border-slate-700/80 bg-[#131a2a] shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-800 bg-gradient-to-r from-[#161f32] to-[#131a2a] px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/15 text-blue-400">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-100">Ruthie</p>
                <p className="text-[11px] text-slate-500">Onboarding assistant</p>
              </div>
            </div>
            <button data-testid="assistant-close" onClick={() => setOpen(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => (
              <div key={i} data-testid={`assistant-message-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "user" ? "rounded-br-sm bg-blue-600 text-white" : "rounded-bl-sm border border-slate-700/70 bg-slate-800/50 text-slate-200"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm border border-slate-700/70 bg-slate-800/50 px-3.5 py-2.5 text-sm text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
                </div>
              </div>
            )}

            {/* Suggestion chips (only before the user asks anything) */}
            {messages.length <= 1 && suggestions.length > 0 && !sending && (
              <div className="flex flex-wrap gap-2 pt-1">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`assistant-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-blue-500 hover:text-blue-300"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-800 bg-[#0f172a] p-3">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                rows={1}
                placeholder="Ask about getting started…"
                className="max-h-28 flex-1 resize-none rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-blue-500"
              />
              <button
                data-testid="assistant-send"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssistantChat;
